"use client";
import { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { FaFacebook } from "react-icons/fa";
import { MdLogout } from "react-icons/md";

const MetaAccountModal = ({
  isOpen,
  onClose,
  onAccountSelect,
  onDisconnect,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [permissionRequired, setPermissionRequired] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
    }
  }, [isOpen]);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    setPermissionRequired(false);
    try {
      const response = await fetch("/api/meta/accounts");
      const data = await response.json();

      console.log("📊 Meta accounts API response:", data);

      if (data.success) {
        setAccounts(data.accounts || []);
        setPermissionRequired(data.permission_required || false);
        setNeedsReconnect(data.needs_reconnect || false);
        if (data.error) {
          setError(data.error);
        }

        // Log debug info if available
        if (data.debug) {
          console.log("🔍 Debug info:", data.debug);
        }

        // Log account count
        if (data.accounts && data.accounts.length > 0) {
          console.log("✅ Found accounts:", data.accounts.length);
          console.log(
            "📋 Account IDs:",
            data.accounts.map((a) => a.id)
          );
        } else {
          console.log(
            "⚠️ No accounts found. needs_reconnect:",
            data.needs_reconnect
          );
        }
      } else {
        setError(data.error || "Failed to fetch accounts");
        setAccounts([]);
        setNeedsReconnect(false);
      }
    } catch (error) {
      console.error("❌ Error fetching accounts:", error);
      setError("Network error. Please try again.");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = async () => {
    if (!selectedAccount) return;

    setSaving(true);
    try {
      const response = await fetch("/api/meta/accounts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ selectedAccount }),
      });

      const data = await response.json();

      if (data.success) {
        onAccountSelect(selectedAccount);
        onClose();
      } else {
        console.error("Failed to save account:", data.error);
      }
    } catch (error) {
      console.error("Error saving account:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !confirm(
        "Are you sure you want to disconnect Meta Ads? This will remove all saved data and you will need to reconnect."
      )
    ) {
      return;
    }

    setDisconnecting(true);
    try {
      const response = await fetch("/api/integrations/meta/disconnect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hardDelete: true,
          revokeToken: true,
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (onDisconnect) onDisconnect();
        onClose();
      } else {
        alert("Failed to disconnect: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error disconnecting:", error);
      alert("Failed to disconnect. Please try again.");
    } finally {
      setDisconnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FaFacebook className="text-[#1877F2] text-xl" />
            <h2 className="text-lg font-semibold text-gray-900">
              Select Meta Ad Account
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Choose which Meta Ads account you want to connect for tracking.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#284F9E]"></div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-8">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-gray-700 font-medium mb-2">
              No Meta ad accounts found
            </p>
            {permissionRequired ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4 text-left">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  Business Manager Access Required
                </p>
                <p className="text-xs text-yellow-700 mb-3">
                  Your app is connected but needs access to your ad accounts
                  through Business Manager.
                </p>
                <div className="bg-white rounded p-3 mb-3">
                  <p className="text-xs font-semibold text-yellow-900 mb-2">
                    Step-by-step instructions:
                  </p>
                  <ol className="text-xs text-yellow-800 space-y-2 list-decimal list-inside">
                    <li>
                      Go to{" "}
                      <a
                        href="https://business.facebook.com/settings/partners"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-medium"
                      >
                        Business Settings → Partners
                      </a>
                    </li>
                    <li>
                      Click <strong>&quot;Add&quot;</strong> button → Select{" "}
                      <strong>
                        &quot;Give a partner access to your assets&quot;
                      </strong>
                    </li>
                    <li>
                      Enter your App ID:{" "}
                      <code className="bg-gray-100 px-1 rounded">
                        {process.env.NEXT_PUBLIC_FB_CLIENT_ID || "Your App ID"}
                      </code>
                    </li>
                    <li>
                      Select <strong>&quot;Ad accounts&quot;</strong> and choose
                      your ad account (PIXCON AD Account)
                    </li>
                    <li>
                      Grant <strong>&quot;Full control&quot;</strong> or{" "}
                      <strong>&quot;View&quot;</strong> permissions
                    </li>
                    <li>
                      Click <strong>&quot;Save changes&quot;</strong>
                    </li>
                    <li>
                      <strong className="text-red-600">IMPORTANT:</strong> After
                      granting access, you <strong>MUST reconnect</strong> your
                      Meta account to get a new token
                    </li>
                    <li>
                      Click <strong>&quot;Reconnect Meta&quot;</strong> button
                      below
                    </li>
                  </ol>
                </div>
                <div className="bg-red-100 border border-red-300 rounded p-3 mt-3">
                  <p className="text-xs text-red-900 font-semibold mb-2">
                    🔴 Critical Issue:
                  </p>
                  <p className="text-xs text-red-800 mb-2">
                    Your current token only has basic permissions (
                    <code>email</code>, <code>public_profile</code>). Business
                    Manager partner access requires{" "}
                    <strong>System User Token</strong> or app-level
                    authentication.
                  </p>
                  <p className="text-xs text-red-800 mb-3">
                    <strong>Current Status:</strong> Token permissions
                    don&apos;t include Business Manager access. Even after
                    granting partner access, user tokens can&apos;t access
                    Business Manager accounts directly.
                  </p>
                  <div className="bg-white rounded p-2 mb-2">
                    <p className="text-xs font-semibold text-red-900 mb-1">
                      Solution Options:
                    </p>
                    <ol className="text-xs text-red-800 space-y-1 list-decimal list-inside">
                      <li>
                        <strong>System User Token:</strong> Setup required in
                        Facebook Developer Dashboard
                      </li>
                      <li>
                        <strong>Reconnect:</strong> May work if Facebook grants
                        additional permissions (try first)
                      </li>
                    </ol>
                  </div>
                  <button
                    onClick={() => {
                      window.location.href =
                        "/api/meta/oauth-url?source=connectivity";
                    }}
                    className="w-full mt-2 px-3 py-2 text-xs text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                  >
                    Try Reconnect Meta →
                  </button>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-left">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  No ad accounts found
                </p>
                <p className="text-xs text-blue-700 mb-3">
                  Your Facebook account is connected, but we couldn&apos;t find
                  any ad accounts. This usually means:
                </p>
                <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside mb-3">
                  <li>
                    The app doesn&apos;t have access to your Business Manager ad
                    accounts
                  </li>
                  <li>
                    You need to grant access through Business Settings →
                    Partners
                  </li>
                </ul>
                <div className="bg-white rounded p-3">
                  <p className="text-xs font-semibold text-blue-900 mb-2">
                    Quick fix:
                  </p>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>
                      Go to{" "}
                      <a
                        href="https://business.facebook.com/settings/partners"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-medium"
                      >
                        Business Settings → Partners
                      </a>
                    </li>
                    <li>
                      Add this app as a partner with access to your ad accounts
                    </li>
                    <li>Click Refresh button below</li>
                  </ol>
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={fetchAccounts}
                className="flex-1 px-4 py-2 text-sm text-[#284F9E] border border-[#284F9E] rounded-md hover:bg-[#284F9E]/5 transition-colors"
              >
                Refresh
              </button>
              <button
                onClick={() => {
                  // Redirect to reconnect Meta
                  window.location.href =
                    "/api/meta/oauth-url?source=connectivity";
                }}
                className="flex-1 px-4 py-2 text-sm text-white bg-[#284F9E] rounded-md hover:bg-[#1e3a7a] transition-colors"
              >
                Reconnect Meta
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
            {accounts.map((account) => (
              <div
                key={account.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedAccount?.id === account.id
                    ? "border-[#284F9E] bg-[#284F9E]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setSelectedAccount(account)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{account.name}</p>
                    <p className="text-sm text-gray-500">ID: {account.id}</p>
                    {account.currency && (
                      <p className="text-xs text-gray-400">
                        Currency: {account.currency}
                      </p>
                    )}
                    {account.account_status && (
                      <p className="text-xs text-gray-400">
                        Status:{" "}
                        {account.account_status === 1 ? "Active" : "Inactive"}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <div
                      className={`w-4 h-4 rounded-full border-2 ml-2 ${
                        selectedAccount?.id === account.id
                          ? "border-[#284F9E] bg-[#284F9E]"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedAccount?.id === account.id && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {/* Disconnect button */}
          {!loading && (
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="w-full px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MdLogout className="w-4 h-4" />
              {disconnecting ? "Disconnecting..." : "Disconnect Meta Ads"}
            </button>
          )}

          {/* Select buttons */}
          {accounts.length > 0 && (
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAccountSelect}
                disabled={!selectedAccount || saving}
                className="flex-1 px-4 py-2 bg-[#284F9E] text-white rounded-md hover:bg-[#1e3a7a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Select Account"}
              </button>
            </div>
          )}

          {/* Close if no accounts */}
          {accounts.length === 0 && !loading && (
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaAccountModal;
